import React, { memo } from 'react'

import BaseCard from '../shared/baseCard/BaseCard'
import Text from '../shared/text/Text'
import DefaultText from '../shared/text/DefaultText'
import { formatEth } from '../../util/numbers.helpers'

const TermsAndConditions = ({
  t,
  mainProfile: { preSaleDepositPrice, publicSaleDepositPrice }
}) => {
  return (
    <BaseCard>
      <Text
        variant='h2'
        label={t('home.terms-and-conditions')}
        fontWeight={700}
      />
      <DefaultText
        label={
          <>
            <p>
              <strong>Effective Date:</strong> 10 September 2024
            </p>

            <h3>1. Introduction</h3>
            <p>
              This document outlines the terms and conditions governing the QBX
              Public Community Token Sale (&quot;Token Sale&quot;) for QBX
              (&quot;Tokens&quot;), which will be conducted via a raffle-based
              allocation system. Participants (&quot;You&quot; or
              &quot;Participants&quot;) agree to abide by these terms by
              engaging in the sale.
            </p>
            <p>
              By depositing Ethereum (ETH) in exchange for raffle tickets,
              Participants may be allocated a set number of tokens based on the
              results of a raffle draw. This document outlines the key terms,
              conditions, and processes related to the raffle, withdrawals, and
              token distribution.
            </p>

            <h3>2. Token Sale Overview</h3>
            <ul>
              <li>
                <strong>Token Name:</strong> QBX
              </li>
              <li>
                <strong>Token Symbol:</strong> QBX
              </li>
              <li>
                <strong>Pre-Sale Raffle Ticket Price:</strong> 1 ticket ={' '}
                {formatEth(preSaleDepositPrice)}
              </li>
              <li>
                <strong>Pre-Sale Raffle Ticket Allocation:</strong> Each ticket
                represents a chance to win a token allocation of 31,089 QBX.
              </li>
              <li>
                <strong>Public-Sale Raffle Ticket Price:</strong> 1 ticket ={' '}
                {formatEth(publicSaleDepositPrice)}
              </li>
              <li>
                <strong>Public-Sale Raffle Ticket Allocation:</strong> Each
                ticket represents a chance to win a token allocation of 2,000
                QBX.
              </li>
              <li>
                <strong>Token Sale Start Date:</strong> September 10th, 2024
              </li>
            </ul>

            <h3>3. Eligibility</h3>
            <p>To participate in the Token Sale, Participants must:</p>
            <ul>
              <li>Be at least 18 years of age or older.</li>
              <li>
                Comply with the applicable laws of their respective
                jurisdiction.
              </li>
              <li>
                Not be a resident or citizen of any jurisdiction in which
                participation in the Token Sale is prohibited or restricted by
                law (such as the USA, North Korea, etc.).
              </li>
            </ul>

            <h3>4. How the Raffle-Based Token Sale Works</h3>

            <h3>4.1 Ticket Purchase Process</h3>
            <ul>
              <li>
                Participants deposit ETH in exchange for pre-sale and
                public-sale raffle tickets, each corresponding to a set amount
                of token allocation.
              </li>
              <li>
                Raffle tickets can be received during the sale period by sending
                the desired amount of ETH to the provided smart contract
                address.
              </li>
              <li>
                Once the ETH is deposited, Participants will receive the
                corresponding number of pre-sale and public-sale raffle tickets.
              </li>
            </ul>

            <h3>4.2 Raffle Draw</h3>
            <ul>
              <li>
                After the Token Sale concludes, a random raffle draw will be
                conducted, starting with the pre-sale tickets and then the
                public sale tickets.
              </li>
              <li>
                The draw will determine which raffle tickets win the token
                allocation associated with each ticket.
              </li>
              <li>
                The raffle results will be made available publicly on the Token
                Sale results page after the raffle draw phase is completed.
              </li>
            </ul>

            <h3>4.3 Allocation of Tokens</h3>
            <ul>
              <li>
                Winning tickets will be entitled to the pre-defined allocation
                of QBX tokens.
              </li>
              <li>
                The token allocation for each winning ticket will be 31,089 QBX
                for pre-sale tickets and 2,000 QBX for public-sales tickets.
              </li>
            </ul>

            <h3>4.4 Withdrawal Process for Non-Winning Tickets</h3>
            <ul>
              <li>
                For any tickets that do not win the raffle, Participants can
                withdraw the ETH deposited for those tickets.
              </li>
              <li>
                Withdrawals will be made available through the sale’s smart
                contract after the raffle results are published on the Token
                Sale raffle results page.
              </li>
              <li>
                Withdrawals must be made within 30 days from the date of the
                raffle results.
              </li>
            </ul>

            <h3>5. Deposit Terms</h3>
            <ul>
              <li>
                All deposits for raffle tickets must be made in Ethereum (ETH).
              </li>
              <li>
                The ETH will be locked in a smart contract for the duration of
                the Token Sale until the raffle results are published.
              </li>
              <li>
                The deposited ETH cannot be withdrawn prior to the end of the
                Token Sale and thereafter only for the raffle tickets that did
                not win.
              </li>
            </ul>

            <h3>6. Token Distribution</h3>
            <ul>
              <li>
                Tokens will be distributed to the Ethereum wallet addresses
                associated with the winning raffle tickets.
              </li>
              <li>
                Tokens will be claimable by Participants at TGE, on a secure QBX
                token claim page.
              </li>
              <li>
                Tokens will be subject to the following vesting schedules: 20%
                unlocked at TGE, 80% with a 1 month cliff and unlocked linearly
                over 12 months.
              </li>
            </ul>

            <h3>7. Withdrawal Process</h3>
            <ul>
              <li>
                Withdraws for non-winning raffle tickets will be handled via the
                smart contract, ensuring transparency and fairness.
              </li>
              <li>
                Withdraws must be claimed within 30 days after the raffle
                results are published.
              </li>
              <li>
                Participants who fail to claim their withdrawals within the
                given period forfeit their withdrawal rights.
              </li>
            </ul>

            <h3>8. Risks</h3>
            <p>
              By participating in the Token Sale, Participants acknowledge the
              following risks:
            </p>
            <ul>
              <li>
                <strong>Volatility Risk:</strong> The value of ETH and tokens
                may fluctuate, resulting in potential financial losses.
              </li>
              <li>
                <strong>Regulatory Risk:</strong> The legal status of blockchain
                technology and digital tokens may change over time, which could
                impact the availability or legal standing of the tokens.
              </li>
              <li>
                <strong>Smart Contract Risk:</strong> While the sale is
                conducted through an audited smart contract, Participants
                acknowledge the possibility of unforeseen technical errors,
                hacks, or vulnerabilities.
              </li>
            </ul>
            <h3>9. Disclaimers</h3>
            <ul>
              <li>
                Participation in the Token Sale does not grant any rights to
                governance or any other utility in QBX, qiibee foundation,
                qiibee ltd. or any associated parties or assets, unless
                otherwise specified.
              </li>
              <li>
                The organizers of the Token Sale do not make any guarantees
                regarding the future value or success of the token.
              </li>
              <li>
                The Token Sale is subject to change or cancellation at any time
                with notice.
              </li>
            </ul>
            <h3>10. Amendments</h3>
            <p>
              The organizers of the Token Sale reserve the right to amend these
              terms at any time. Any changes will be communicated to
              Participants via official channels.
            </p>
            <h3>11. Jurisdiction and Governing Law</h3>
            <p>
              This Agreement and any dispute arising out of or in connection
              with the Token Sale shall be governed by and construed in
              accordance with the laws of Zug, Switzerland.
            </p>
            <hr />
            <strong>
              By participating in the Token Sale, you agree to the above terms
              and conditions.
            </strong>
            <hr />
            <h3>Contact Information:</h3>
            <p>
              <strong>qiibee foundation</strong>
            </p>
            <p>
              <a
                href='mailto:foundationtcpp@qiibee.com'
                style={{ color: 'inherit' }}
              >
                foundationtcpp@qiibee.com
              </a>
            </p>
            <p>Alpenstrasse 7, 6300 Zug, Switzerland</p>
          </>
        }
      />
    </BaseCard>
  )
}

export default memo(TermsAndConditions)
